import { Component, computed, inject, input } from '@angular/core';
import { marked } from 'marked';
import { ReportRunState } from '../report-run-state';
import { AISummaryDisplay, AISummaryOutput } from '../../../models/runnable_report';

@Component({
    selector: 'risr-features-reports-report-ai-summary',
    template: `
        <section class="ai-summary" role="region" [attr.aria-label]="ariaLabel()">
            @if (title()) {
                <h3 class="ai-summary-title">{{ title() }}</h3>
            }
            @if (description()) {
                <p class="ai-summary-description">{{ description() }}</p>
            }

            @if (error(); as message) {
                <p class="box box-danger" role="alert">{{ message }}</p>
            } @else if (content(); as text) {
                @switch (format()) {
                    @case ('html') {
                        <div class="ai-summary-content" [innerHTML]="text"></div>
                    }
                    @case ('markdown') {
                        <div class="ai-summary-content" [innerHTML]="renderedMarkdown()"></div>
                    }
                    @default {
                        <div class="ai-summary-content ai-summary-text">{{ text }}</div>
                    }
                }
                @if (cached()) {
                    <p class="ai-summary-meta" i18n>Cached result</p>
                }
            } @else {
                <p class="ai-summary-empty" i18n>No summary was generated.</p>
            }
        </section>
    `,
    styles: `
        .ai-summary-text {
            white-space: pre-wrap;
        }

        .ai-summary-meta {
            color: #6c757d;
            font-size: 0.85em;
            margin-top: 0.5em;
        }
    `,
})
export class ReportAISummary {
    readonly reportDisplayItem = input.required<AISummaryDisplay>();

    private readonly reportRunState = inject(ReportRunState);

    private readonly output = computed<AISummaryOutput | undefined>(() =>
        this.reportRunState.getOutputResult<AISummaryOutput>(this.reportDisplayItem().id)()
    );

    protected readonly title = computed(() => this.reportDisplayItem().title);
    protected readonly description = computed(() => this.reportDisplayItem().description);
    protected readonly ariaLabel = computed(() => this.title() || $localize`AI summary`);

    protected readonly format = computed(() => this.output()?.format ?? 'text');
    protected readonly content = computed(() => this.output()?.content ?? null);
    protected readonly error = computed(() => this.output()?.error ?? null);
    protected readonly cached = computed(() => this.output()?.cached ?? false);

    protected readonly renderedMarkdown = computed(() => {
        const text = this.content();
        if (!text) {
            return '';
        }
        return marked.parse(text, { async: false });
    });
}
